import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { EmptyState, KpiCard, KpiStrip } from '../cc'
import {
  reportCard, reportCardPad, reportCardSub, reportCardTitle, reportLoading,
  reportPill, reportSectionSub, reportSectionTitle, reportSegment, reportSegmentBtn,
} from '../reports/ui'

interface DimensionRollup {
  incidents: number
  resolved: number
  still_open: number
  vehicles: number
  median_hours: number | null
  max_idle_hours: number | null
  avg_km_away: number | null
  trips_lost: number
  revenue_lost_inr: number
}

interface SnapshotData {
  from: string
  to: string
  fleet_daily_trip_target: number
  target_trips_in_period: number
  revenue_per_trip_inr: number
  totals: DimensionRollup
  by_category: Record<string, DimensionRollup>
  by_severity: Record<string, DimensionRollup>
  by_hub_proximity: Record<string, DimensionRollup>
}

type Period = 'daily' | 'weekly' | 'monthly'

const SEVERITY_TONE: Record<string, 'ok' | 'warn' | 'danger'> = {
  minor: 'ok', major: 'warn', immobilised: 'danger',
}

function formatInr(v: number): string {
  return `₹${Math.round(v).toLocaleString('en-IN')}`
}

/**
 * Ranked by revenue lost, descending -- "what's costing us the most" is the question a
 * fault-category table answers; sorted any other way buries the answer in an alphabetical
 * list. Rows with zero loss are still shown (a category with incidents but no downtime
 * conversion is itself informative), just sink to the bottom.
 */
function RankedLossTable({
  title, rows, labelHeader,
}: {
  title: string
  rows: [string, DimensionRollup][]
  labelHeader: string
}): JSX.Element {
  const sorted = [...rows].sort(([, a], [, b]) => b.revenue_lost_inr - a.revenue_lost_inr)
  return (
    <div className={`${reportCard} overflow-hidden`}>
      <div className="border-b border-slate-100 px-4 py-3">
        <h4 className={reportCardTitle}>{title}</h4>
      </div>
      {sorted.length === 0 ? (
        <p className="px-4 py-6 text-center text-[12px] text-slate-400">No incidents in this window.</p>
      ) : (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-slate-100 text-[10px] font-medium uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 text-left">{labelHeader}</th>
              <th className="px-2 py-2 text-right">Incidents</th>
              <th className="px-2 py-2 text-right">Trips lost</th>
              <th className="px-2 py-2 text-right">Revenue lost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {sorted.map(([label, r]) => (
              <tr key={label} className="transition-colors duration-100 hover:bg-slate-50/60">
                <td className="px-4 py-2 font-medium text-slate-700">
                  {SEVERITY_TONE[label] ? <span className={reportPill(SEVERITY_TONE[label])}>{label}</span> : label}
                </td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">{r.incidents}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-500">{r.trips_lost}</td>
                <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-rose-600">
                  {formatInr(r.revenue_lost_inr)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/**
 * The revenue answer to "why didn't we hit 65 trips today" -- every breakdown's own
 * downtime, converted to a lost round trip and a rupee figure, ranked by what actually
 * costs the most. Lived buried inside the Breakdown report's Preview toggle before this;
 * that made the numbers correct but not a report anyone could find, hence its own tab.
 */
export function TripRevenueLoss(): JSX.Element {
  const [period, setPeriod] = useState<Period>('weekly')
  const [data, setData] = useState<SnapshotData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    fetch(`/api/breakdowns/snapshot?period=${period}`)
      .then(async (r) => {
        const body = await r.json() as SnapshotData & { error?: string }
        if (!r.ok || body.error) throw new Error(body.error ?? String(r.status))
        if (alive) setData(body)
      })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : String(e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [period])

  const t = data?.totals
  const pctOfTarget = data && t && data.target_trips_in_period > 0
    ? Math.round((t.trips_lost / data.target_trips_in_period) * 1000) / 10
    : null

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className={reportSectionTitle}>Trip &amp; revenue loss</h1>
      <p className={reportSectionSub}>
        What breakdown downtime actually costs — each incident's own downtime converted to
        a lost round trip (÷24h) and a revenue figure (38 MT × ₹500/MT = ₹19,000/trip),
        summed and ranked by what costs the most. Fleet target: {data?.fleet_daily_trip_target ?? 65} round trips/day.
      </p>

      <div className="mt-4">
        <div className={reportSegment}>
          {(['daily', 'weekly', 'monthly'] as const).map((p) => (
            <button
              key={p} type="button" onClick={() => setPeriod(p)}
              className={reportSegmentBtn(period === p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={`mt-4 flex items-center gap-2 ${reportLoading}`}>
          <Loader2 size={14} className="animate-spin" /> Building the {period} loss report…
        </div>
      ) : error ? (
        <div className={`mt-4 ${reportCard} ${reportCardPad}`}>
          <p className="text-[13px] text-rose-600">Report unavailable — {error}</p>
        </div>
      ) : data && t ? (
        <div className="mt-4 space-y-4">
          <KpiStrip>
            <KpiCard
              label="Round trips lost" value={t.trips_lost} unit="trips"
              tone={t.trips_lost > 0 ? 'danger' : 'ok'} dot
            />
            <KpiCard
              label="Revenue lost" value={formatInr(t.revenue_lost_inr)}
              tone={t.revenue_lost_inr > 0 ? 'danger' : 'ok'}
            />
            <KpiCard
              label="Fleet target, this window" value={data.target_trips_in_period} unit="trips"
              sub={`${data.fleet_daily_trip_target}/day`}
            />
            <KpiCard
              label="Share of target lost" value={pctOfTarget != null ? `${pctOfTarget}%` : '—'}
              tone={pctOfTarget != null && pctOfTarget > 5 ? 'warn' : 'neutral'}
            />
          </KpiStrip>

          <div className={`${reportCardSub} px-1`}>
            {t.incidents} incident{t.incidents === 1 ? '' : 's'} · {t.resolved} resolved
            {t.still_open > 0 && <span className="text-rose-600"> · {t.still_open} still down</span>}
            {' '}· {t.vehicles} vehicle{t.vehicles === 1 ? '' : 's'} affected
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <RankedLossTable title="By fault category" labelHeader="Category"
              rows={Object.entries(data.by_category)} />
            <RankedLossTable title="By severity" labelHeader="Severity"
              rows={Object.entries(data.by_severity)} />
          </div>

          {t.incidents === 0 ? (
            <EmptyState
              title="No breakdown downtime in this window"
              body="No trips or revenue lost to breakdowns — nothing to rank."
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
